import type { ApiError, ApiResponse, PaginatedResponse } from "@/types";
import { API_BASE_URL } from "@/lib/constants";

export interface RequestOptions {
  idempotencyKey?: string;
}

type ErrorEnvelope = {
  message?: string;
  code?: string;
  details?: Record<string, string[] | string>;
};

function fieldHintsFrom(details: ErrorEnvelope["details"]): string[] {
  if (!details || typeof details !== "object") {
    return [];
  }

  return Object.entries(details)
    .filter(([field]) => field !== "requestId")
    .flatMap(([field, messages]) => {
      const list = Array.isArray(messages) ? messages : [String(messages)];

      return list.map((message) =>
        field === "_root" ? message : `${field}: ${message}`,
      );
    });
}

function normalizedDetails(
  details: ErrorEnvelope["details"],
): Record<string, string[]> | undefined {
  if (!details || typeof details !== "object") {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(details).map(([field, messages]) => [
      field,
      Array.isArray(messages) ? messages : [String(messages)],
    ]),
  );
}

async function fetchOrThrowNetworkError(
  sendRequest: () => Promise<Response>,
): Promise<Response> {
  try {
    return await sendRequest();
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? err.message : "Network request failed",
    );
  }
}

function redirectToLogin() {
  const alreadyOnLoginPage =
    typeof window === "undefined" || window.location.pathname === "/login";

  if (!alreadyOnLoginPage) {
    window.location.href = "/login";
  }
}

async function errorFromResponse(response: Response): Promise<ApiClientError> {
  const raw = await response.json().catch(() => null);
  const envelope: ErrorEnvelope = raw?.error ?? raw ?? {};
  const serverMessage =
    envelope.message || raw?.message || `Request failed (${response.status})`;

  const hints = fieldHintsFrom(envelope.details);
  const message =
    hints.length > 0 ? `${serverMessage} — ${hints.join("; ")}` : serverMessage;

  return new ApiClientError(message, response.status, {
    message: serverMessage,
    code: envelope.code,
    details: normalizedDetails(envelope.details),
  });
}

function unwrapEnvelope<T>(body: unknown): T {
  if (body && typeof body === "object" && "pagination" in body) {
    return body as T;
  }

  if (body && typeof body === "object" && "data" in body) {
    return (body as { data: T }).data;
  }

  return body as T;
}

class ApiClient {
  private baseUrl: string;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async refreshSession(): Promise<boolean> {
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = fetch(`${this.baseUrl}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    requestOptions: RequestOptions = {},
  ): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string> | undefined),
    };

    if (requestOptions.idempotencyKey) {
      headers["Idempotency-Key"] = requestOptions.idempotencyKey;
    }

    const sendRequest = () =>
      fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
        credentials: "include",
      });

    let response = await fetchOrThrowNetworkError(sendRequest);

    if (response.status === 401) {
      const refreshed = await this.refreshSession().catch(() => false);

      if (!refreshed) {
        redirectToLogin();

        throw new ApiClientError("Unauthorized", 401);
      }

      response = await fetchOrThrowNetworkError(sendRequest);
    }

    if (!response.ok) {
      throw await errorFromResponse(response);
    }

    if (response.status === 204) return {} as T;

    return unwrapEnvelope<T>(await response.json());

  }

  async get<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>,
  ): Promise<T> {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) searchParams.append(key, String(value));
      });
      const qs = searchParams.toString();
      if (qs) url += `?${qs}`;
    }

    return this.request<T>(url, { method: "GET" });
  }

  async post<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: "POST",
        body: data ? JSON.stringify(data) : undefined,
      },
      options ?? {},
    );
  }

  async put<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: "PUT",
        body: data ? JSON.stringify(data) : undefined,
      },
      options ?? {},
    );
  }

  async patch<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: "PATCH",
        body: data ? JSON.stringify(data) : undefined,
      },
      options ?? {},
    );
  }

  async delete<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: "DELETE",
        body: data ? JSON.stringify(data) : undefined,
      },
      options ?? {},
    );
  }

  async login(username: string, password: string) {
    const response = await fetch(`${this.baseUrl}/auth/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: "Login failed" }));

      throw new ApiClientError(
        error.message || "Login failed",
        response.status,
        error,
      );
    }

    const result = await response.json();

    return result.data;
  }

  async loginWithPin(username: string, pin: string) {
    const response = await fetch(`${this.baseUrl}/auth/login-pin`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, pin }),
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: "Login failed" }));

      throw new ApiClientError(
        error.message || "Login failed",
        response.status,
        error,
      );
    }

    const result = await response.json();

    return result.data;
  }

  async logout(): Promise<void> {
    try {
      await fetch(`${this.baseUrl}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
    }
  }
}

export class ApiClientError extends Error {
  status: number;
  details?: ApiError;

  constructor(message: string, status: number, details?: ApiError) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.details = details;
  }
}

export class NetworkError extends Error {
  constructor(message = "Network request failed") {
    super(message);
    this.name = "NetworkError";
  }
}

export function isRetryableFailure(error: unknown): boolean {
  if (error instanceof NetworkError) {
    return true;
  }

  if (!(error instanceof ApiClientError)) {
    return false;
  }

  return error.status >= 500 || error.status === 408 || error.status === 429;
}

export const apiClient = new ApiClient(API_BASE_URL);

export type { ApiResponse, PaginatedResponse };
